import { useState, useEffect } from "react";
import useApi from "@/hooks/useApi";

interface AdminFeatures {
  enableQuickStartWizards: boolean;
  enableMcp: boolean;
  maxMcpServers: number;
  /** Max MCP tool calls per single conversation (prevents runaway loops). Default: 20. */
  maxMcpCallsPerConversation: number;
  /** Monthly MCP tool call budget across all conversations. Default: 1000. */
  maxMcpCallsPerMonth: number;
  /** Enables AI-to-AI delegation and section jumps in workflow AI nodes. */
  enableOrchestration: boolean;
  /** Enables Page Widgets — lightweight slide-out widgets on customer pages. */
  enablePageWidgets: boolean;
  /** Native HubSpot CRM integration — contact search + transcript push. */
  enableHubSpot: boolean;
  /** Native BigCommerce e-commerce integration — customer/order/product search. */
  enableBigCommerce: boolean;
  /** Native Microsoft Dynamics 365 CRM integration — contact/account/lead + rules engine. */
  enableDynamics: boolean;
  /** Maximum deployments allowed for this site. Default: 1. */
  maxDeployments: number;
  /** Current number of active (non-deleted) deployments. */
  currentDeployments: number;
  /** WebRTC P2P video chat for agents and visitors. */
  enableVideoChat: boolean;
  /** OneDrive/SharePoint file attachment sharing in agent chat conversations. Requires M365 connected. */
  enableOneDriveAttachments: boolean;
  /** Microsoft Teams channel notifications via incoming webhooks (resolve, new chat, missed, SLA breach). */
  enableTeamsNotifications: boolean;
  /** Slack channel notifications via incoming webhooks (resolve, new chat, missed, SLA breach). */
  enableSlackNotifications: boolean;
  /** Outlook Calendar booking + Calendly integration. Default: true (all plans). */
  enableCalendarBooking: boolean;
  /** Native Magento / Adobe Commerce e-commerce integration. */
  enableMagento: boolean;
  /** Native Velaro CRM — deals, contacts, companies, activities, buying signals, and 24 AI tools. */
  enableNativeCrm: boolean;
}

const DEFAULT_FEATURES: AdminFeatures = {
  enableQuickStartWizards: true,
  enableMcp: true,
  maxMcpServers: 3,
  maxMcpCallsPerConversation: 20,
  maxMcpCallsPerMonth: 1000,
  enableOrchestration: false,
  enablePageWidgets: false,
  enableHubSpot: false,
  enableBigCommerce: false,
  enableDynamics: false,
  maxDeployments: 1,
  currentDeployments: 0,
  enableVideoChat: false,
  enableOneDriveAttachments: false,
  enableTeamsNotifications: false,
  enableSlackNotifications: false,
  enableCalendarBooking: true,
  enableMagento: false,
  enableNativeCrm: false
};

/**
 * Returns v20-specific feature flags from the admin backend.
 * These are distinct from the v10 subscription flags in useSubscriptions().
 * All flags default to true so the app never accidentally locks users out.
 */
export default function useAdminFeatures(): AdminFeatures {
  const api = useApi();
  const [features, setFeatures] = useState<AdminFeatures>(DEFAULT_FEATURES);

  useEffect(() => {
    api.admin
      .get("Features")
      .then((res) => res.json())
      .then((data) => {
        setFeatures({
          enableQuickStartWizards: data?.enableQuickStartWizards ?? true,
          enableMcp: data?.enableMcp ?? true,
          maxMcpServers: data?.maxMcpServers ?? 3,
          maxMcpCallsPerConversation: data?.maxMcpCallsPerConversation ?? 20,
          maxMcpCallsPerMonth: data?.maxMcpCallsPerMonth ?? 1000,
          enableOrchestration: data?.enableOrchestration ?? false,
          enablePageWidgets: data?.enablePageWidgets ?? false,
          enableHubSpot: data?.enableHubSpot ?? false,
          enableBigCommerce: data?.enableBigCommerce ?? false,
          enableDynamics: data?.enableDynamics ?? false,
          maxDeployments: data?.maxDeployments ?? 1,
          currentDeployments: data?.currentDeployments ?? 0,
          enableVideoChat: data?.enableVideoChat ?? false,
          enableOneDriveAttachments: data?.enableOneDriveAttachments ?? false,
          enableTeamsNotifications: data?.enableTeamsNotifications ?? false,
          enableSlackNotifications: data?.enableSlackNotifications ?? false,
          enableCalendarBooking: data?.enableCalendarBooking ?? true,
          enableMagento: data?.enableMagento ?? false,
          enableNativeCrm: data?.enableNativeCrm ?? false
        });
      })
      .catch(() => {
        // Non-fatal — keep defaults (all features on)
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return features;
}
